import React, { useRef, useEffect, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Text, Environment, PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';

// Floating particles component
function FloatingParticles({ count = 100 }) {
  const meshRef = useRef();
  const [points, setPoints] = useState(null);

  useEffect(() => {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
    }
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const material = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 0.02,
      transparent: true,
      opacity: 0.6
    });
    
    const pointsObject = new THREE.Points(geometry, material);
    meshRef.current = pointsObject;
    setPoints(pointsObject);
  }, [count]);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.001;
      meshRef.current.rotation.x += 0.0005;
    }
  });

  // Only render if points object exists
  if (!points) return null;
  
  return <primitive object={points} />;
}

// Story book component
function StoryBook({ position, rotation }) {
  const meshRef = useRef();

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime) * 0.1;
    }
  });

  return (
    <group position={position} rotation={rotation}>
      {/* Book cover */}
      <mesh ref={meshRef}>
        <boxGeometry args={[2, 3, 0.2]} />
        <meshStandardMaterial color="#8B4513" />
      </mesh>
      
      {/* Book pages */}
      <mesh position={[0, 0, 0.11]}>
        <boxGeometry args={[1.8, 2.8, 0.05]} />
        <meshStandardMaterial color="#FFF8DC" />
      </mesh>
      
      {/* Glowing effect */}
      <pointLight position={[0, 0, 0.5]} intensity={0.5} color="#FFD700" />
    </group>
  );
}

// Floating text component
function FloatingText({ text, position, color = "#ffffff" }) {
  const textRef = useRef();

  useFrame((state) => {
    if (textRef.current) {
      textRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime + position[0]) * 0.2;
    }
  });

  return (
    <Text
      ref={textRef}
      position={position}
      fontSize={0.5}
      color={color}
      anchorX="center"
      anchorY="middle"
      font="/fonts/inter.woff"
    >
      {text}
    </Text>
  );
}

// Main story scene
function StoryScene3D({ storyContent }) {
  const { camera } = useThree();

  useEffect(() => {
    camera.position.set(0, 2, 8);
    camera.lookAt(0, 0, 0);
  }, [camera]);

  return (
    <>
      {/* Environment */}
      <Environment preset="sunset" />
      
      {/* Lighting */}
      <ambientLight intensity={0.4} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <pointLight position={[-10, -10, -5]} intensity={0.5} color="#4A90E2" />
      
      {/* Floating particles */}
      <FloatingParticles count={150} />
      
      {/* Story book */}
      <StoryBook position={[0, 0, 0]} rotation={[0, 0, 0]} />
      
      {/* Floating text elements */}
      <FloatingText text="Story" position={[-3, 2, 0]} color="#FFD700" />
      <FloatingText text="Learning" position={[3, 1.5, 0]} color="#4A90E2" />
      <FloatingText text="Outcomes" position={[3, 0.5, 0]} color="#4A90E2" />
      <FloatingText text="Keywords" position={[-3, -1, 0]} color="#E24A90" />
      <FloatingText text="Concepts" position={[0, -2, 0]} color="#90E24A" />
      
      {/* Magical sparkles */}
      <pointLight position={[2, 2, 2]} intensity={0.3} color="#FFD700" />
      <pointLight position={[-2, -2, 2]} intensity={0.3} color="#4A90E2" />
    </>
  );
}

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('3D Scene Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontSize: '18px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div>
            <h3>3D Scene Loading...</h3>
            <p>Please wait while the 3D environment initializes.</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main component
const StoryScene = ({ storyContent }) => {
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <ErrorBoundary>
        <Canvas
          camera={{ position: [0, 2, 8], fov: 60 }}
          style={{ background: 'transparent' }}
          onCreated={({ gl }) => {
            gl.setClearColor(0x000000, 0);
          }}
        >
          <StoryScene3D storyContent={storyContent} />
          <OrbitControls
            enablePan={false}
            enableZoom={true}
            enableRotate={true}
            minDistance={5}
            maxDistance={15}
            minPolarAngle={Math.PI / 6}
            maxPolarAngle={Math.PI / 2}
          />
        </Canvas>
      </ErrorBoundary>
    </div>
  );
};

export default StoryScene;


